import { useCallback } from 'react';
import {
  useCreateWorkOrderDocument,
  useDeleteWorkOrderDocument,
  dataUrlToBase64,
  getFileExtensionFromDataUrl,
  base64ToDataUrl
} from '@api/enterprise/workOrderDocuments';
import { WorkOrderDocumentType } from '@api/enterprise/openapi';
import {
  findSignatureDocument as findDoc,
  parseSignatureData,
  formatSignatureText,
  type SignatureMode
} from '../utils/signatureDocumentUtils';
import { SIGNATURE_DOCUMENT } from '../constants/signatureDocumentConstants';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { enqueue } from '@services/offlineSyncService';

type UseSignatureDocumentParams = {
  jobNumber: string;
  userName: string;
  printedName: string;
  existingDocuments: any[] | undefined;
  onSignatureLoaded: (params: {
    dataUrl: string;
    timestamp: string;
    mode: SignatureMode;
    name: string;
  }) => void;
};

export const useSignatureDocument = ({
  jobNumber,
  userName,
  printedName,
  existingDocuments,
  onSignatureLoaded
}: UseSignatureDocumentParams) => {
  const isOnline = useOnlineStatus();
  const { mutate: createDocument, isPending: isCreating } =
    useCreateWorkOrderDocument();
  const { mutateAsync: deleteDocument, isPending: isDeleting } =
    useDeleteWorkOrderDocument();

  const isSubmittingDocument = isCreating || isDeleting;

  const findSignatureDocument = useCallback(() => {
    return findDoc(existingDocuments);
  }, [existingDocuments]);

  const loadSignatureFromDocument = useCallback(
    (document: {
      fileAttachment?: string | null | undefined;
      additionDate?: string | null | undefined;
      signatureText?: string | null;
    }) => {
      if (!document.fileAttachment) return;

      const dataUrl = base64ToDataUrl(document.fileAttachment);
      const timestamp = document.additionDate || new Date().toISOString();
      const { mode, name } = parseSignatureData(document.signatureText || '');

      onSignatureLoaded({ dataUrl, timestamp, mode, name });
    },
    [onSignatureLoaded]
  );

  const submitSignatureDocument = useCallback(
    async (dataUrl: string, mode: SignatureMode) => {
      const base64Data = dataUrlToBase64(dataUrl);
      const fileExtension = getFileExtensionFromDataUrl(dataUrl);

      const documentData = {
        jobNumber,
        documentCompany: SIGNATURE_DOCUMENT.COMPANY,
        documentName: SIGNATURE_DOCUMENT.NAME,
        documentType: WorkOrderDocumentType.CustomerSignature,
        addedBy: userName.toUpperCase(),
        fileName: `SIGNATURE.${fileExtension.toUpperCase()}`,
        fileAttachment: base64Data,
        signatureText: formatSignatureText(mode, printedName)
      };

      if (!isOnline) {
        await enqueue({
          entityType: 'wo_document_create',
          entityKey: `${jobNumber}:${SIGNATURE_DOCUMENT.NAME}`,
          payload: {
            jobNumber,
            documentData,
            replaceExisting: !!findSignatureDocument()
          },
          priority: 50
        });
        return;
      }

      const existingDoc = findSignatureDocument();

      if (existingDoc) {
        try {
          await deleteDocument({
            jobNumber,
            documentCompany: SIGNATURE_DOCUMENT.COMPANY,
            documentName: SIGNATURE_DOCUMENT.NAME
          });
        } catch {}
      }

      createDocument({
        jobNumber,
        documentData,
        showToast: false
      });
    },
    [
      findSignatureDocument,
      deleteDocument,
      createDocument,
      jobNumber,
      userName,
      printedName,
      isOnline
    ]
  );

  const submitBypassedSignature = useCallback(
    async (reason: 'bypassed' | 'skipped' = 'bypassed') => {
      if (reason === 'skipped') {
        if (!isOnline) return;

        const existingDoc = findSignatureDocument();
        if (existingDoc) {
          try {
            await deleteDocument({
              jobNumber,
              documentCompany: SIGNATURE_DOCUMENT.COMPANY,
              documentName: SIGNATURE_DOCUMENT.NAME
            });
          } catch {}
        }
        return;
      }

      const documentData = {
        jobNumber,
        documentCompany: SIGNATURE_DOCUMENT.COMPANY,
        documentName: SIGNATURE_DOCUMENT.NAME,
        documentType: WorkOrderDocumentType.CustomerSignature,
        addedBy: userName.toUpperCase(),
        signatureText: 'bypassed:Customer acknowledgment bypassed'
      };

      if (!isOnline) {
        await enqueue({
          entityType: 'wo_document_create',
          entityKey: `${jobNumber}:${SIGNATURE_DOCUMENT.NAME}`,
          payload: {
            jobNumber,
            documentData,
            replaceExisting: !!findSignatureDocument()
          },
          priority: 50
        });
        return;
      }

      const existingDoc = findSignatureDocument();

      if (existingDoc) {
        try {
          await deleteDocument({
            jobNumber,
            documentCompany: SIGNATURE_DOCUMENT.COMPANY,
            documentName: SIGNATURE_DOCUMENT.NAME
          });
        } catch {}
      }

      createDocument({
        jobNumber,
        documentData,
        showToast: false
      });
    },
    [
      findSignatureDocument,
      deleteDocument,
      createDocument,
      jobNumber,
      userName,
      isOnline
    ]
  );

  return {
    findSignatureDocument,
    loadSignatureFromDocument,
    submitSignatureDocument,
    submitBypassedSignature,
    isSubmittingDocument
  };
};
